import { useCallback, useEffect, useState } from 'react';
import {
  listCustomerTasks,
  type CustomerTask,
} from '../../infra/customer360Repo';

export type { CustomerTask };

const TASKS_PAGE_SIZE = 5;

export function useCustomerTasks(
  customerId: string | null,
  visible: boolean,
) {
  const [tasks, setTasks] = useState<CustomerTask[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(
    async (newPage: number) => {
      if (!customerId) return;
      setLoading(true);
      try {
        const rows = await listCustomerTasks(
          customerId,
          TASKS_PAGE_SIZE,
          newPage * TASKS_PAGE_SIZE,
        );
        setTasks(rows);
        setPage(newPage);
        setHasMore(rows.length === TASKS_PAGE_SIZE);
      } catch (err) {
        console.warn('[useCustomerTasks] Error loading page', err);
        setTasks([]);
        setPage(0);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [customerId],
  );

  const reload = useCallback(() => loadPage(0), [loadPage]);

  useEffect(() => {
    if (!visible || !customerId) {
      setTasks([]);
      setPage(0);
      setHasMore(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void loadPage(0).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [customerId, visible, loadPage]);

  return {
    tasks,
    loading,
    page,
    hasMore,
    pageSize: TASKS_PAGE_SIZE,
    loadPage,
    reload,
  };
}
